import { getTokenDefinition, } from "@bracket/chainography";
import type { AmountString, WEIString, TokenIdentifier, } from "@bracket/types";


export const toWeiString = ( v: bigint, token: TokenIdentifier, ): WEIString => {
	const tok = getTokenDefinition(token);
	const { decimals } = tok;
	const result = (Math.floor((Number(v) / (10**decimals)) * 100000) / 100000);
	if(result <= 0) {
		return "0";
	}
	return result.toFixed(5);
}

export declare type AmountToBigIntOptions = {
	dustoff?: boolean;
}

const amountToBigIntDefaults: AmountToBigIntOptions = {
	dustoff: true,
}



/**
 * Converts a decimal amount string to a bigint value with the correct number of decimals for a token
 * @description Inside a javascript runtime using bigint, we should ALWAYS dust off the extra decimals as Number does not have the fidelity to support bigint values
 * @param {AmountString} v - The amount string to convert (e.g. "1.23")
 * @param {TokenIdentifier} token - The token identifier to get decimals from
 * @param {AmountToBigIntOptions} [options] - Optional configuration
 * @param {boolean} [options.dustoff=true] - Whether to remove dust (extra decimals) from the result
 * @returns {bigint} The amount as a bigint with the correct number of decimals
 */
export const amountToBigInt = ( v: AmountString, token: TokenIdentifier, options: AmountToBigIntOptions = amountToBigIntDefaults ): bigint => {

	// in case we're sent a string with only the floating point part
	if(["", "."].includes(v)) {
		return BigInt(0);
	}

	const { dustoff, } = { ...amountToBigIntDefaults, ...options };
	const tok = getTokenDefinition(token);
	const { decimals } = tok;

	const [i, f] = v.includes(".") ? v.split(".").map(v => v.length) : [v.length, 0];

	let result = BigInt(Number(Number(v)*(10**decimals)));

	if(dustoff && f > 0) {
		const rstring = result.toString();
		const subpart = result.toString().substring(0, i+f);
		result = BigInt(rstring.split("").map((v,i) => i > subpart.length ? "0" : v).join(""));
	}

	return result;
}

export const toNumeric = ( v: bigint, token: TokenIdentifier, ): number => {
	const tok = getTokenDefinition(token);
	const { decimals } = tok;
	return Number(v) / (10**decimals);
}


export const raise = ( m: string, ): never => {
	throw new Error(m);
}
